import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
} from "../../../Common";
import {
  CheckoutModel_MyBag,
  CheckoutModel_MyBag_Graphql_Request,
} from "../../model";

import { useGenericDataPortForMyBag } from "../use-generic-data-port-for-mybag";

import { DataMutation_Shipping_SetGuestEmail_QueryKey } from "./data-mutation--shipping-set-guest-email--query-key-factory";

export interface DataMutationShippingSetGuestEmailConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<
    DataMutationShippingSetGuestEmail_Result["data"]
  > {
  data: DataMutationShippingSetGuestEmail_Result["data"];
  status: DataMutationShippingSetGuestEmail_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationShippingSetGuestEmailConsumerInterface;
const DataMutationShippingSetGuestEmailContext =
  React.createContext(defaultValue);

export interface DataMutationShippingSetGuestEmail_ControllerInterface {
  mutate: DataMutationShippingSetGuestEmail_Result["mutate"];
  mutateAsync: DataMutationShippingSetGuestEmail_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationShippingSetGuestEmail_ControllerInterface;
const DataMutationShippingSetGuestEmail_ControllerContext = React.createContext(
  controllerDefaultValue
);

export interface DataMutationShippingSetGuestEmail_Request {
  cartId: string;
  email: string;
}
export type DataMutationShippingSetGuestEmail_Result =
  ReactQuery.UseMutationResult<
    CheckoutModel_MyBag,
    unknown,
    DataMutationShippingSetGuestEmail_Request,
    unknown
  >;
export type GraphqlResponse_ShippingSetGuestEmail = GraphqlWrappedData<
  "setGuestEmailOnCart",
  {
    cart: CheckoutModel_MyBag;
  }
>;

export interface DataMutationShippingSetGuestEmailProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationShippingSetGuestEmailProvider: React.FunctionComponent<
  DataMutationShippingSetGuestEmailProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const [genericDataPortValues, genericDataPortController] =
    useGenericDataPortForMyBag();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutation_Shipping_SetGuestEmail_QueryKey.shippingSetGuestEmail();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    CheckoutModel_MyBag,
    DataMutationShippingSetGuestEmail_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = `
        mutation {
          setGuestEmailOnCart(
            input: {
              cart_id: "${request.cartId}"
              email: "${request.email}"
            }
          ) {
            cart {
              ${CheckoutModel_MyBag_Graphql_Request}
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponse_ShippingSetGuestEmail>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      genericDataPortController.portError(data);

      const { data: gqlResponseData } = data || {};
      const { setGuestEmailOnCart } = gqlResponseData || {};
      const { cart } = setGuestEmailOnCart || {};

      genericDataPortController.portSuccess();

      return cart;
    },
    [initialState, genericDataPortController]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: genericDataPortController.useMutationOrQueryOnSuccess,
  });

  const ignoreMyCurrentState =
    genericDataPortController.ignoreMyCurrentStateFactory(mutation);
  const value: DataMutationShippingSetGuestEmailConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        status: mutation.status,
        error: mutation.error,

        dataUpdatedAt: genericDataPortValues.dataUpdatedAt,
        errorUpdatedAt: genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState: ignoreMyCurrentState,
      }),
      [
        mutation.data,
        mutation.variables,
        mutation.status,
        mutation.error,

        genericDataPortValues.dataUpdatedAt,
        genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState,
      ]
    );
  const controllerValue: DataMutationShippingSetGuestEmail_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationShippingSetGuestEmailContext.Provider value={value}>
      <DataMutationShippingSetGuestEmail_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationShippingSetGuestEmail_ControllerContext.Provider>
    </DataMutationShippingSetGuestEmailContext.Provider>
  );
};

export const useDataMutationShippingSetGuestEmail = () => {
  const context = React.useContext(DataMutationShippingSetGuestEmailContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationShippingSetGuestEmail must be used within a DataMutationShippingSetGuestEmailProvider"
    );
  }
  return context;
};

export const useDataMutationShippingSetGuestEmail_asController = () => {
  const context = React.useContext(
    DataMutationShippingSetGuestEmail_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationShippingSetGuestEmail_asController must be used within a DataMutationShippingSetGuestEmailProvider"
    );
  }
  return context;
};
